import { fetchStackFramesPreviewForRunKey } from '@cotera/client/app/hooks/stack-frame-fetch';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { DG } from '@cotera/sdk/core';
import React, { Suspense, useCallback, useContext, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ModelScopeFactory,
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
  Watchable,
} from '../../actions/framework';
import { LlmModelContext } from '../../columns/llm/context';
import { GraphModel } from '../../graph/graph.model';
import { BaseCellRenderer, DefaultCell } from '../cell';
import { CellComponentPropsWithModel } from '../types';
import { AtcCellModel, mapPreviewToFull } from './cell.model';
import { AtcContext, useAtcCellModel } from './context';
import { AtcSuspensefulCellComponentForRunKey } from './shared';
import { RunAtcCellAction } from './run-atc-cell.action';

const NEVER = Watchable.fromValue(false);

export const PreviewAtcCellComponent = (
  props: CellComponentPropsWithModel & {
    getEditorState: () => DG.DocumentEditor.DocumentState;
    relName: string;
    cellRenderer?: React.ComponentType<CellComponentPropsWithModel>;
    atcColumns: string[];
    llmConfig: {
      model: string;
      schedule: 'never' | 'daily' | 'hourly' | { cron: string };
      temperature: number;
      maxOutputTokens: number;
    };
    isPreviewMode?: boolean;
  }
) => {
  const llmModel = useContext(LlmModelContext);

  // Whether user has clicked run-on-new-rows this session
  // TODO future: can reset this to false after data loads
  const runOnNewRowsClicked = useWatchableValue(
    llmModel?.runOnNewRowsClicked ?? NEVER
  );

  const runPreviewAction = useAction(RunAtcCellAction);
  const graphModel = useInScopeModel(GraphModel);
  const client = useTenantedClient();
  const llmConfig = useMemo(() => {
    return {
      model: props.llmConfig.model,
      when: props.llmConfig.schedule,
      temperature: props.llmConfig.temperature,
      maxOutputTokens: props.llmConfig.maxOutputTokens,
    };
  }, [
    props.llmConfig.model,
    props.llmConfig.schedule,
    props.llmConfig.temperature,
    props.llmConfig.maxOutputTokens,
  ]);

  const columnType = props.column.t;
  const isAtcColumn = props.atcColumns.includes(props.column.key);
  const cellModelId = `atc-cell-${props.relName}-${props.column.key}-${props.rowIndex}`;
  const fetchStackFrames = useCallback(
    async (runKey: string) => {
      const result = await fetchStackFramesPreviewForRunKey(client.atc, runKey);
      return result.map(mapPreviewToFull);
    },
    [client]
  );
  const createModels = useCallback(
    () =>
      isAtcColumn
        ? [
            new AtcCellModel(
              props.rowIndex,
              props.column.key,
              props.relName,
              props.rowData?.['__cotera_stable_id'],
              {
                model: llmConfig.model,
                when: llmConfig.when,
                temperature: llmConfig.temperature,
                maxOutputTokens: llmConfig.maxOutputTokens,
                topP: 1,
                topK: 1,
                frequencyPenalty: 0,
                presencePenalty: 0,
              },
              props.getEditorState,
              graphModel.graph,
              props.isPreviewMode ?? false,
              fetchStackFrames,
              props.value
            ),
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAtcColumn,
      props.rowIndex,
      props.column.key,
      props.relName,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      props.rowData?.['__cotera_stable_id'],
      llmConfig,
      props.isPreviewMode,
      props.getEditorState,
      graphModel.graph,
      fetchStackFrames,
    ]
  );

  if (isAtcColumn) {
    return (
      <ModelScopeFactory createModels={createModels} deps={[cellModelId]}>
        <PreviewAtcCellContent
          {...props}
          cellModelId={cellModelId}
          runPreviewAction={runPreviewAction}
          runOnNewRowsClicked={runOnNewRowsClicked}
        />
      </ModelScopeFactory>
    );
  }

  if (columnType === 'eraql') {
    //not in yet rel - must be a newly created column
    if (props.value === undefined) {
      return (
        <DefaultCell {...props}>
          <Badge variant={'muted'}>Run dataset to see value</Badge>
        </DefaultCell>
      );
    }
  }

  return <BaseCellRenderer {...props} />;
};

const PreviewAtcCellContent = (
  props: CellComponentPropsWithModel & {
    getEditorState: () => DG.DocumentEditor.DocumentState;
    relName: string;
    cellRenderer?: React.ComponentType<CellComponentPropsWithModel>;
    atcColumns: string[];
    llmConfig: {
      model: string;
      schedule: 'never' | 'daily' | 'hourly' | { cron: string };
      temperature: number;
      maxOutputTokens: number;
    };
    isPreviewMode?: boolean;
    cellModelId: string;
    runPreviewAction: ReturnType<typeof useAction>;
    runOnNewRowsClicked: boolean;
  }
) => {
  const cellModel = useInScopeModel(AtcCellModel, props.cellModelId);
  const actionsContext = useActionsContext();

  return (
    <AtcContext.Provider value={{ model: cellModel }}>
      <ErrorBoundary
        fallbackRender={() => {
          return (
            <DefaultCell {...props}>
              <Badge variant={'muted'}>Client Error</Badge>
            </DefaultCell>
          );
        }}
      >
        <Suspense
          fallback={
            <DefaultCell {...props}>
              {props.value === undefined ? (
                <Badge variant={'muted'} className="animate-pulse">
                  Loading
                </Badge>
              ) : (
                JSON.stringify(props.value, null, 2)
              )}
            </DefaultCell>
          }
        >
          <PreviewAtcSuspensefulCellComponent
            {...props}
            cellRenderer={
              (props.cellRenderer ??
                BaseCellRenderer) as React.ComponentType<CellComponentPropsWithModel>
            }
            onRun={async () => {
              actionsContext.targetModel(cellModel);
              await props.runPreviewAction.execute();
              actionsContext.markAsNotTargeted(cellModel);
            }}
            shimmerIfNull={props.runOnNewRowsClicked}
          />
        </Suspense>
      </ErrorBoundary>
    </AtcContext.Provider>
  );
};

const PreviewAtcSuspensefulCellComponent = (
  props: CellComponentPropsWithModel & {
    cellRenderer: React.ComponentType<CellComponentPropsWithModel>;
    onRun: () => void;
    shimmerIfNull?: boolean;
  }
) => {
  const cellModel = useAtcCellModel();
  const runKey = useWatchableValue(cellModel.runKey);
  const colIdentity = useWatchableValue(cellModel.colIdentity);
  const startingIdentity = useWatchableValue(cellModel.startingIdentity);

  if (runKey === null) {
    return <props.cellRenderer {...props} />;
  }
  return (
    <AtcSuspensefulCellComponentForRunKey
      {...props}
      runKey={runKey}
      startingIdentity={startingIdentity}
      currentIdentity={colIdentity}
      onPlay={props.onRun}
    />
  );
};
